import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  agentAssignmentApi,
  AssignedAgent,
} from "@/services/api/agentAssignment";

interface AgentAssignmentState {
  assignedAgent: AssignedAgent | null;
  isLoading: boolean;
  error: string | null;
  assignmentStatus: "idle" | "pending" | "assigned" | "failed";
}

const initialState: AgentAssignmentState = {
  assignedAgent: null,
  isLoading: false,
  error: null,
  assignmentStatus: "idle",
};

// Async thunks
export const assignAgent = createAsyncThunk(
  "agentAssignment/assignAgent",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentAssignmentApi.assignAgent();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to assign agent"
      );
    }
  }
);

export const fetchAssignedAgent = createAsyncThunk(
  "agentAssignment/fetchAssignedAgent",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentAssignmentApi.getAssignedAgent();
      return response.data;
    } catch (error: any) {
      // Handle 404 "No agent assigned" as a successful response with no agent
      if (
        error.response?.status === 404 &&
        error.response?.data?.message === "No agent assigned"
      ) {
        return null; // Return null to indicate no agent assigned
      }

      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch assigned agent"
      );
    }
  }
);

export const reassignAgent = createAsyncThunk(
  "agentAssignment/reassignAgent",
  async (
    { customerId, newAgentId }: { customerId: string; newAgentId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await agentAssignmentApi.reassignAgent(
        customerId,
        newAgentId
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reassign agent"
      );
    }
  }
);

const agentAssignmentSlice = createSlice({
  name: "agentAssignment",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAssignedAgent: (state) => {
      state.assignedAgent = null;
      state.assignmentStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Assign agent
      .addCase(assignAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.assignmentStatus = "pending";
      })
      .addCase(assignAgent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assignedAgent = action.payload.data?.assignedAgent || null;
        state.assignmentStatus = action.payload.success ? "assigned" : "failed";
        state.error = null;
      })
      .addCase(assignAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.assignmentStatus = "failed";
      })

      // Fetch assigned agent
      .addCase(fetchAssignedAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssignedAgent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assignedAgent = action.payload;
        state.assignmentStatus = action.payload ? "assigned" : "idle";
        state.error = null;
      })
      .addCase(fetchAssignedAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.assignmentStatus = "idle";
      })

      // Reassign agent
      .addCase(reassignAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(reassignAgent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assignedAgent = action.payload.data?.assignedAgent || null;
        state.assignmentStatus = action.payload.success ? "assigned" : "failed";
        state.error = null;
      })
      .addCase(reassignAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.assignmentStatus = "failed";
      });
  },
});

export const { clearError, clearAssignedAgent } = agentAssignmentSlice.actions;
export default agentAssignmentSlice.reducer;
